import {
    ChallengeLocationSummary,
    CreatePointRequest,
    CreateTagRequest,
    CreateTopicRequest,
    CreateTopicResponse,
    CustomFieldUrlType,
    DirectionType,
    DiscoveryLevel,
    GetPointResponse,
    IntegrationException,
    MapDrawer,
    ObjectId,
    PendingVote,
    TopicCreationSource,
    URLType,
} from '../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { addOpenTableClick, addTopic, getTopic } from '../helpers/api/topics-api';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined, isValidString } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { addPoint } from '../helpers/api/points-api';
import challengeData from '../helpers/state/challenge-data';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { isUrlValidAndSafe } from '../helpers/strings';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

interface CustomButtonData {
    fieldId: string;
    urlSettings: CustomFieldUrlType;
    buttonColor: string;
    buttonIconUrl: string;
    buttonText: string;
    openInNewTab: boolean;
    fieldValue: string;
}

@customElement('point-card-actions')
export class PointCardActions extends LitElement {
    static amplitude_callsite: string = 'Point Card Actions';



    @property({ type: Object })
    point?: GetPointResponse;

    @property({ type: Boolean })
    hideCreateMap: boolean = false; // when this is passed in, we set showCreateMap to false

    @property({ type: Boolean })
    centered: boolean = false;

    @property({ type: String })
    directionsMode?: string;

    @state()
    showCheckinButton: boolean = false;

    @state()
    showCreateMap: boolean = false;

    @state()
    showDirections: boolean = true;

    // mostly an overwrite for list page
    @state()
    hideCheckin: boolean = false;

    @state()
    showUrl: boolean = false;

    @state()
    showOpenTable: boolean = false;

    @state()
    showInquire: boolean = false;

    @state()
    inquireOpened: boolean = false;

    @state()
    challengeLocation: ChallengeLocationSummary | undefined;

    @state()
    noPadding: boolean = false;

    @state()
    get hasActions() {
        return this.showCreateMap || this.showDirections || this.showUrl || this.showOpenTable || this.showInquire || this.showCheckinAction || this.hasElection || this.hasCustomButtons;
    }

    // The topic's "Mark as Visited" toggle only governs the casual visited button —
    // a challenge location's Check In belongs to the challenge and stays available.
    get showCheckinAction() {
        return this.showCheckinButton || (isDefined(this.challengeLocation) && !this.hideCheckin && topicData.hasExceptions !== IntegrationException.ProsperityMarket);
    }

    @state()
    hasElection: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindChallengeData = new StateController(this, challengeData);

    firstUpdated() {
        // if you are making changes here, dont forget to make them also in the updated section
        if (topicData.discoveryLevel !== DiscoveryLevel.Private && topicData.topic?.disable_add_to_map !== true && !this.hideCreateMap) {
            this.showCreateMap = true;
        }

        if (topicData.topic?.disable_get_directions !== true && this.showDirections) {
            this.showDirections = true;
        } else {
            this.showDirections = false;
        }

        if (topicData.topic?.disable_mark_as_visited !== true && topicData.hasExceptions !== IntegrationException.ProsperityMarket && !this.hideCheckin) {
            this.showCheckinButton = true;
        }

        if (topicData.topic?.disable_more_info !== true && topicData.topic?.add_point_customization?.more_info?.hide_on_point_card !== true && isValidString(this.point?.url)) {
            this.showUrl = true;
        }

        if (topicData.topic?.point_email_capture_customization?.is_active === true) {
            this.showInquire = true;
        }

        switch (topicData.topic?.type_get_directions) {
            case DirectionType.drive:
                this.directionsMode = 'd';
                break;
            case DirectionType.walk:
                this.directionsMode = 'w';
                break;
            case DirectionType.bike:
                this.directionsMode = 'b';
                break;
            case DirectionType.transit:
                this.directionsMode = 'r';
                break;
            default:
                this.directionsMode = 'd';
                break;
        }
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('point') || changedProperties.has('challengeLocation')) {
            if (topicData.topic?.disable_more_info !== true && topicData.topic?.add_point_customization?.more_info?.hide_on_point_card !== true && isValidString(this.point?.url)) {
                this.showUrl = true;
            }

            if (topicData.topic?.disable_book_experiences !== true && isDefined(this.point?.opentable_info)) {
                this.showOpenTable = true;
            }
        }

        // Re-check challenge location when point changes or when challenge data becomes available
        if (isDefined(this.point) && isDefined(challengeData.activatedChallengeId) && isDefined(challengeData.challenge)) {
            const found = challengeData.challengeLocations.find( (cl) => cl.point_info?._id.$oid === this.point?._id.$oid);
            if (found !== this.challengeLocation) {
                this.challengeLocation = found;
            }
        }
    }

    private toggleInquire() {
        this.dispatchEvent(
            new CustomEvent('on-toggle-form', {
                detail: {
                    point: this.point
                },
                bubbles: true,
                composed: true
            })
        );

        this.inquireOpened = !this.inquireOpened;
    }

    async handleClickCheckIn() {
        if (!this.point) {
            console.error('Point is undefined');
            getMainPage().errorToastElement?.showToast('Check-in failed: point not loaded yet. Please refresh and try again.');
            return;
        }
        // Check the challenge page context first: if we're on the challenge page,
        // the map component may not be mounted even though pointCache.map is still
        // populated from a prior map view. Routing to the map branch in that case
        // silently no-ops on the optional chain.
        if (isDefined(challengeData.challenge_page)) {
            // Challenge data loads via a long async chain; until it's ready, the
            // check-in drawer routes to the wrong step (sign-in / has-challenge)
            // and appears to do nothing. Tell the user instead of silently failing.
            if (!challengeData.isReady) {
                getMainPage().errorToastElement?.showToast('Just a moment — still loading…');
                return;
            }
            // Look up challenge location at click time in case it wasn't set during updated()
            const location = this.challengeLocation ?? challengeData.challengeLocations.find(
                (cl) => cl.point_info?._id.$oid === this.point?._id.$oid
            );
            if (!isDefined(location)) {
                getMainPage().errorToastElement?.showToast('This location isn\'t ready to check in yet. Please try again.');
                return;
            }
            // check that it is not already pending
            const isPending = challengeData.activated_challenge?.pending_checkin_location_ids?.includes(location.challenge_location_info?._id?.$oid ?? '') === true;
            if (isPending) {
                getMainPage().errorToastElement?.showToastAndThrow('You have already submitted a check-in for this location. Please wait for it to be approved.');
                return;
            }
            await challengeData.challenge_page.showLocationConfirmCheckin();
        } else if (isDefined(pointCache.map) && isDefined(topicData.mapComponent)) {
            const challengeIsHappening = isDefined(topicData.challengeHappeningNow) && topicData.challengeHappeningNow.is_live;
            const isChallengeLocation = challengeIsHappening &&  this.challengeLocation?.challenge_location_info?.is_active !== false;
            await topicData.mapComponent.onToggleCheckin(this.point, pointCache.isUserCheckedIn(this.point), challengeIsHappening, isChallengeLocation);
            logWithAmplitude(PointCardActions.amplitude_callsite, 'Visited Click', {
                point_id: this.point?._id.$oid,
                topic_id: topicData.topicId,
            });
        } else {
            getMainPage().errorToastElement?.showToast(
                `Check-in couldn't open — no map or challenge page available. (point=${this.point._id.$oid} hasPointCacheMap=${String(isDefined(pointCache.map))} hasMapComponent=${String(isDefined(topicData.mapComponent))} hasChallengePage=${String(isDefined(challengeData.challenge_page))})`
            );
        }
        this.requestUpdate();
    }

    async handleClickElect() {
        if (!this.point) return;
        const pendingVote: PendingVote = {
           selected_points: [this.point?._id],
           topic_category_id: this.point?.topic_category_id,
        };
        await topicData.mapComponent?.onToggleElection(pendingVote);
        logWithAmplitude(PointCardActions.amplitude_callsite, 'Vote for Point Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId,
        });
    }


    async getWhatsAppLink() {
        // first one is their destination map
        if (topicData.topicId === '67b45460f2c6f6544bfb827c') {
            if (!this.point) return;
            const text = `Hi, I am interested in the property ${this.point.name ?? this.point.location.search} seen in your digital portfolio ${window.location.href}`;
            const encodedText = encodeURIComponent(text);
            // https://wa.me/whatsappphonenumber?text=urlencodedtext
            return `https://wa.link/6t8kzr?text=${encodedText}`;
        } else {
            if (!this.point) return;
            const text = `Hi, I am interested in the property ${this.point.name ?? this.point.location.search} seen in your digital portfolio ${window.location.href}`;
            const encodedText = encodeURIComponent(text);
            // https://wa.me/whatsappphonenumber?text=urlencodedtext
            return `https://wa.me/447525824645?text=${encodedText}`;
        }
    }

    get isCheckedIn() {
        return this.point ? pointCache.isUserCheckedIn(this.point) || (this.challengeLocation?.checked_in ?? false) : false;
    }

    render() {
        if (this.hasActions) {
            return html`
                <div class="actions__wrapper" style="${this.centered ? 'justify-content: center;' : ''}">
                    ${this.showInquire
                        ? html` <form-button
                              raised
                              size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                              .textColor=${topicData.textColor}
                              class="actions__button"
                              @click="${this.toggleInquire}"
                                icon="${this.inquireOpened ? 'close' : 'email'}"
                            >
                                ${this.inquireOpened
                                    ? html`<translated-span>Close Form</translated-span>`
                                    : html`<translated-span>${topicData.topic?.point_email_capture_customization?.cta_button_text ?? 'Inquire Now'}</translated-span>`}
                            </form-button>`
                        : ''}
                    ${topicData.hasExceptions === IntegrationException.JLLRealEstate  // tk JLL shameless hack
                    ? html`
                        <form-button
                            raised
                            size="sm"
                            .backgroundColor=${topicData.backgroundColor}
                            .textColor=${topicData.textColor}
                            class="actions__button whatsapp-button"
                            @click="${async () => {
                                window.open(await this.getWhatsAppLink(), topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
                            }}"
                        >
                        <img src="/images/socials/whatsapp_white.png" class="whatsapp-icon" alt="Checked In icon" />

                        </form-button>
                    ` : ''
                    }


                    ${this.showCheckinAction && this.challengeLocation?.challenge_location_info?.is_active !== false && isDefined(this.point)
                        ? this.isCheckedIn
                            ? html` <form-button
                                  raised
                                  size="sm"
                                    .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                                  class="actions__button actions__button--checkin ${isDefined(this.challengeLocation) ? 'red' : ''}"
                                  ?disabled=${isDefined(challengeData.challenge_page) && !challengeData.isReady}
                                  @click="${this.handleClickCheckIn}"
                              >
                                  <img
                                      src="/images/ic_checked_in.png"
                                      class="action__icon--checked_in"
                                      alt="Checked In icon"
                                  />
                                  ${isDefined(this.challengeLocation)
                                      ? html`<translated-span>${challengeData.challenge?.button_text ?? 'Check In'}</translated-span> <translated-span>Again</translated-span>`
                                      : html`<translated-span>${topicData.topic?.text_visited ?? 'Mark as Visited'}</translated-span>`}
                              </form-button>`
                            : html` <form-button
                                size="sm"
                                    .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                                  raised
                                  .icon=${'check_box_outline_blank'}
                                  class="actions__button actions__button--checkin ${isDefined(this.challengeLocation)  ? 'red' : ''}"
                                  ?disabled=${isDefined(challengeData.challenge_page) && !challengeData.isReady}
                                  @click="${this.handleClickCheckIn}"
                              >
                                  ${isDefined(this.challengeLocation)
                                      ? html`<translated-span>${challengeData.challenge?.button_text ?? 'Check In'}</translated-span>`
                                      : html`<translated-span>${topicData.topic?.text_mark_as_visited ?? 'Mark as Visited'}</translated-span>`}
                              </form-button>`
                        : ''}
                    ${isDefined(topicData.electionHappeningNow) && this.point?._id && topicData.mapComponent?.mapDrawer !== MapDrawer.ElectionBallot && !topicData.electionHappeningNow?.excluded_categories.map((c) => c.$oid).includes(this.point?.topic_category_id.$oid)
                        ? html` <form-button
                              raised
                              size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                            .textColor=${topicData.textColor}
                              class="actions__button"
                              @click="${this.handleClickElect}"
                          >
                                <img
                                    src="/images/voted.png"
                                    class="action__icon--open-table"
                                    alt="Vote icon"
                                />
                                <translated-span>
                                    ${topicData.electionHappeningNow.point_button_text ?? 'Vote Now!'}
                                </translated-span>
                          </form-button>`
                        : ''}
                    ${this.showOpenTable
                        ? html` <form-button
                              raised
                              size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                              class="actions__button actions__button--book-a-table"
                              @click="${this.openOpenTableLink}"
                          >
                              <img
                                  src="/images/socials/opentable.png"
                                  class="action__icon--open-table"
                                  alt="OpenTable icon"
                              />
                              <translated-span>Book a Table</translated-span>
                          </form-button>`
                        : ''}
                    ${this.showDirections
                        ? html` <form-button
                              raised
                              size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                              class="actions__button"
                              @click="${this.onDirectionsClick}"
                              icon="directions"
                          >
                              <translated-span>${topicData.topic?.text_get_directions ?? 'Directions'}</translated-span>
                          </form-button>

                          `
                        : ''}
                    ${topicData.topic?.hide_get_route !== true && this.showDirections && this.point?.location.place_id !== 'shape' && featuresInfo.customizeAddPointFormEnabled
                        ? html`
                            <form-button
                              raised
                              size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                              class="actions__button"
                              @click="${this.onRouteClick}"
                              icon="directions"
                          >
                              <translated-span>${'Get Route'}</translated-span>
                          </form-button>`
                        : ''}
                    ${this.showUrl
                        ? html` <form-button
                            size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                                    .textColor=${topicData.textColor}
                              class="actions__button"
                              @click="${this.openUrl}"
                              icon="open_in_new"
                          >
                              <translated-span>${topicData.topic?.text_more_info ?? 'More Info'}</translated-span>
                          </form-button>`
                        : ''}

                    ${this.showCreateMap && !isDefined(this.challengeLocation) && accountInfo.isAuthenticated
                        ? html` <form-button
                            size="sm"
                              .backgroundColor=${topicData.backgroundColor}
                                .textColor=${topicData.textColor}
                              class="actions__button"
                              icon="map"
                              @click="${this.onShowAddToMapModal}"
                          >
                              <translated-span>Add to Map</translated-span>
                          </form-button>`
                        : ''}
                    ${this.customButtons.map(buttonData => html`
                        <form-button
                            size="sm"
                            .backgroundColor=${buttonData.buttonColor}
                            class="actions__button"
                            @click="${() => this.onClickCustomButton(buttonData)}"
                        >
                            ${isValidString(buttonData.buttonIconUrl)
                                ? html`<img
                                    src="${buttonData.buttonIconUrl}"
                                    class="action__icon--custom-button"
                                    alt="Custom Button Icon"
                                />`
                                : ''}
                            <translated-span>${buttonData.buttonText}</translated-span>
                        </form-button>
                    `)}
                </div>
                <error-toast id="errorToast"></error-toast>
            `;
        }
    }

    get hasCustomButtons(): boolean {
        const fields = topicData.topic?.add_point_customization?.custom_fields && this.point?.custom_information?.custom_field_entries;
        if (!fields) {
            return false;
        }
        return this.customButtons.length > 0;
    }

    get customButtons(): CustomButtonData[] {
        let customFields = topicData.topic?.add_point_customization?.custom_fields;
        const customFieldEntries = this.point?.custom_information?.custom_field_entries;

        if (!customFields || !customFieldEntries) {
            return [];
        }

        if (topicData.hasExceptions === IntegrationException.HighStreetSafari) {
            // if the user has not paid/donated on their activated challenge do not show the custom buttons
            const activatedChallenge = challengeData.activated_challenge;
            if (isDefined(activatedChallenge) && challengeData.thisUserHasPaidSomething()) {
                // filter custom fields to only those with the name 'Standard AR"
                customFields = customFields.filter(cf => cf.customizations?.header?.toLowerCase().includes('premium'));
            } else {
                // filter custom fields to only those with the name 'Standard AR"
                customFields = customFields.filter(cf => cf.customizations?.header?.toLowerCase().includes('standard'));
            }
        }

        // Create lookup map for O(1) field value access
        const fieldValueMap = new Map<string, string>();
        customFieldEntries.forEach(entry => {
            if (isValidString(entry.field_value)) {
                fieldValueMap.set(entry.field_id.$oid, entry.field_value);
            }
        });

        // Single pass filter + map operation
        return customFields
            .filter(cf => {
                // Early returns for efficiency
                if (!('URL' in cf.field_type) ||
                    cf.archived ||
                    cf.customizations?.hide_on_point_card === true ||
                    cf.field_type.URL?.show_as_button !== true) {
                    return false;
                }

                const fieldId = cf._id?.$oid;
                return fieldId && fieldValueMap.has(fieldId);
            })
        .map(cf => {
            const urlSettings = (cf.field_type as URLType).URL;
            const fieldId = cf._id?.$oid;

            // Guard against undefined fieldId
            if (!fieldId) {
                return null;
            }

            const fieldValue = fieldValueMap.get(fieldId);
            const customInfo = this.point?.custom_information as Record<string, any>;
            const buttonTextKey = `${fieldId}_button_text`;

            const customButtonData: CustomButtonData = {
                fieldId,
                urlSettings,
                buttonColor: urlSettings?.button_color ?? topicData.backgroundColor,
                buttonIconUrl: urlSettings?.button_icon_url ?? '',
                buttonText: customInfo?.[buttonTextKey] ?? urlSettings?.button_text ?? 'Learn More',
                openInNewTab: urlSettings?.open_in_new_tab ?? true,
                fieldValue: fieldValue ?? ''
            };

            return customButtonData;
        }).filter((data): data is CustomButtonData => data !== null);
    }

    private onClickCustomButton(buttonData: CustomButtonData): void {
        if (!this.point) return;

        const url = buttonData.fieldValue.trim();
        if (!isValidString(url)) return;

        // Ensure URL has proper protocol
        const fullUrl = url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`;


        // back up protection in case isValidUrl is not working as expected or is malicious url gets through
        if (!isUrlValidAndSafe(fullUrl)) {
            getMainPage().errorToastElement.showToastAndThrow('The URL for this button is not valid.');
        }

        // Respect topic-level and field-level settings
        const shouldOpenInNewTab = buttonData.openInNewTab ?? (topicData.topic?.open_links_in_same_tab !== true);
        const target = shouldOpenInNewTab ? '_blank' : '_self';

        window.open(fullUrl, target);

        logWithGoogleTagAndTopicOrganization(
            PointCardActions.amplitude_callsite,
            GoogleTagEvent.CUSTOM_BUTTON_CLICKED,
            {
                point_id: this.point._id.$oid,
                point_name: this.point.name,
                field_name: buttonData.buttonText,
                custom_field_id: buttonData.fieldId,
            }
        );
    }

    private onDirectionsClick() {
        if (this.point && isValidString(this.point.location.place_id)) {
            const location = `https://www.google.com/maps?daddr=${encodeURIComponent(
                this.point?.location.search ?? ''
            )}&dirflg=${this.directionsMode ?? 'd'}&saddr=My+Location`;
            window.open(location, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');

            logWithAmplitude(PointCardActions.amplitude_callsite, 'Get Directions Click', {
                point_id: this.point?._id.$oid,
                topic_id: topicData.topicId,
            });

            logWithGoogleTagAndTopicOrganization(PointCardActions.amplitude_callsite, GoogleTagEvent.POINT_GET_DIRECTIONS_CLICKED, {
                point_id: this.point?._id.$oid,
                point_name: this.point?.name,
            });
        }
    }

    private async onRouteClick() {
        if (this.point) {
            if (topicData.mapComponent) {
                await topicData.mapComponent.onOpenCreateRoute([this.point]);
                await delay(100);
                topicData.mapComponent.closeListOfPoints();
            }


            logWithAmplitude(PointCardActions.amplitude_callsite, 'Get Route Click', {
                point_id: this.point?._id.$oid,
                topic_id: topicData.topicId,
            });

            logWithGoogleTagAndTopicOrganization(PointCardActions.amplitude_callsite, GoogleTagEvent.POINT_GET_ROUTE_CLICKED, {
                point_id: this.point?._id.$oid,
                point_name: this.point?.name,
            });
        }
    }

    private async onShowAddToMapModal() {
        if (this.point === undefined) {
            return;
        }
        await topicData.mapComponent?.onShowAddToMapModal([this.point]);

        logWithAmplitude(PointCardActions.amplitude_callsite, 'Add To Map Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId,
        });
    }

    private openUrl() {
        if (this.point?.url === undefined) {
            return;
        }

        let url = this.point.url.trim();

        // unless it starts with mailto or tel, add utm_source as proxi_maps, utm_medium at map, utm_campaign as map name with no spaces, utm_content as point name with no spaces
        if (!url.startsWith('mailto:') && !url.startsWith('tel:')) {
            const mapName = topicData.topic?.name?.replace(/\s+/g, '') ?? '';
            const pointName = this.point?.name?.replace(/\s+/g, '') ?? '';

            // Check if URL already has parameters
            const separator = url.includes('?') ? '&' : '?';
            const utmParams = `utm_medium=map&utm_campaign=${mapName}&utm_content=${pointName}&utm_source=proxi_maps`;
            url += `${separator}${utmParams}`;
        }

        if (
            url.startsWith('http://') ||
            url.startsWith('https://') ||
            url.startsWith('mailto:') ||
            url.startsWith('tel:')
        ) {
            window.open(url, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
        } else {
            window.open('https://' + url, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
        }

        logWithAmplitude(PointCardActions.amplitude_callsite, 'More Info Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId,
        });

        logWithGoogleTagAndTopicOrganization(PointCardActions.amplitude_callsite, GoogleTagEvent.POINT_MORE_INFO_CLICKED, {
            point_id: this.point?._id.$oid,
            point_name: this.point?.name,
        });
    }

    private async openOpenTableLink() {
        if (this.point?.opentable_info && isValidString(this.point?.opentable_info?.url)) {
            let url = this.point.opentable_info.url;
            if (topicData.hasExceptions === IntegrationException.SeattleGood) {
                // add ?ref=7840 to the end of the URL to track the click from them
                url = `${url}?ref=7840`;
            } else {
                // add ?ref=18582 to the end of the URL to track the click from proxi
                url = `${url}?ref=18582`;
            }
            // Open OpenTable link in tab with our referral code (not secret)
            window.open(url, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
            if (this.point === undefined) return;
            // DO NOT add await here, this will cause the entire page to fail to load if the telemetry server is having issues
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            addOpenTableClick(
                PointCardActions.amplitude_callsite,
                topicData.topicId,
                this.point?._id.$oid
            );
        }
    }

    private async createNewMap() {
        const tag_info: CreateTagRequest = {
            user_tags: [],
        };

        const email = accountInfo.email;

        const createTopicRequest: CreateTopicRequest = {
            name: 'My Proxi Map',
            email,
            organization_id: undefined,
            use_organization_template: false,
            creator: undefined,
            image_display: undefined,
            default_list_view: undefined,
            categories: topicData.categories,
            has_dates: topicData.topic?.has_dates ?? false,
            has_date_only: topicData.topic?.has_date_only ?? false,
            has_time_only: topicData.topic?.has_time_only ?? false,
            show_easy_date_filters: topicData.topic?.show_easy_date_filters ?? true,
            has_end_date_time: topicData.topic?.has_end_date_time ?? true,
            should_filter_past_events: topicData.topic?.should_filter_past_events ?? false,
            has_likes: false,
            discovery_level: DiscoveryLevel.Private,
            disable_google_images: false,
            hide_header: false,
            show_advanced_filter_row: false,
            custom_font: undefined,
            requires_approval: false,
            send_crowdsource_email: true,
            default_map_center: topicData.topic?.default_map_center,
            default_map_zoom: topicData.topic?.default_map_zoom,
            default_map_rotation: topicData.topic?.default_map_rotation,
            default_map_tilt: topicData.topic?.default_map_tilt,
            header_logo: undefined,
            header_url: undefined,
            header_color: undefined,
            header_text_color: undefined,
            cluster_accent_color: undefined,
            translation_languages: undefined,
            custom_map_style: undefined,
            custom_map_style_name: undefined,
            custom_social_media_image: undefined,
            marker_cluster_minimum: undefined,
            marker_cluster_threshold: undefined,
            icon_size: undefined,
            featured_color: undefined,
            featured_name: undefined,
            search_pill_settings: undefined,
            share_settings: topicData.topic?.share_settings,
            custom_map_geojson: undefined,
            custom_map_geojson_style: undefined,
            custom_shapes: topicData.topic?.custom_shapes,
            custom_overlays: topicData.topic?.custom_overlays,
            tag_info,
            creation_source: TopicCreationSource.INTERNAL_FROM_POINT,
            pop_ups: undefined,
            comment_settings: undefined,
            add_point_customization: undefined,
            point_email_capture_customization: undefined,
        };

        let createTopicResponse: CreateTopicResponse;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            createTopicResponse = await addTopic(
                PointCardActions.amplitude_callsite,
                createTopicRequest
            );

            const newTopic = await getTopic(
                PointCardActions.amplitude_callsite,
                createTopicResponse?._id.$oid,
                createTopicResponse.write_key,
                false
            );

            if (this.point === undefined) {
                return;
            }

            const pointCategory = topicData.categories.find(
                (c) => c._id?.$oid === this.point?.topic_category_id.$oid
            );

            const createPointRequest: CreatePointRequest = {
                _id: undefined,
                email,
                topic_id: createTopicResponse._id,
                topic_category_id: newTopic.categories.find(
                    (newCategory) => newCategory.name === pointCategory?.name
                )?._id as ObjectId,
                location: this.point.location,
                name: this.point.name,
                description: this.point.description,
                url: this.point.url,
                special_offer: undefined,
                active: true,
                featured: false,
                start: this.point.start,
                end: this.point.end,
                source: {
                    source_type: 'ProxiMap',
                    source_url: `${window.location.origin}/#discover-details?topic_id=${topicData.topicId}&point_id=${this.point._id.$oid}`,
                    source_name: topicData.name,
                },
                tag_info: this.point.tag_info ?? { user_tags: [], google_tags: [] },
                embedded_video: this.point.embedded_video,
                custom_icon_url: this.point.custom_icon_url,
                is_gate_hidden: this.point.is_gate_hidden,
                custom_information: this.point.custom_information,
                url_images: undefined,
                number: this.point.number,
                custom_shape: this.point.custom_shape,
            };

            await addPoint(
                PointCardActions.amplitude_callsite,
                createTopicResponse?._id.$oid,
                createPointRequest,
                createTopicResponse.write_key
            );

            // Open Link
            if (createTopicResponse.should_use_keyless_links) {
                window.open(createTopicResponse.cc_account_admin_link, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
            } else {
                window.open(createTopicResponse.cc_write_link, topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank');
            }
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 30px;
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-button-horizontal-padding: 8px;
                --mdc-typography-button-font-size: 0.75rem;
            }

            /* width */
            .actions__wrapper::-webkit-scrollbar {
                height: 0px;
            }

            /* Track */
            .actions__wrapper::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .actions__wrapper::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .action__icon--custom-button {
                height: 14px;
                padding-right: 6px;
            }

            .actions__wrapper {
                display: flex;
                margin: 8px 0;
                overflow: auto;
                flex-wrap: wrap;
                gap: 4px;
            }

            .actions__button {
                flex-shrink: 0;
            }

            .action__icon--open-table {
                height: 14px;
                padding-right: 6px;
            }

            .action__icon--checked_in {
                height: 16px;
                padding-right: 6px;
            }

            .whatsapp-icon {
                height: 16px;
            }

        `;
    }
}
